/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { type AxiosResponse, AxiosError } from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name?: string;
  age?: number;
  gender?: Gender;
  role: UserRole;
  indianCdc?: string;
  seafarerSid?: string;
  aadhaarNumber?: string;
  currentShipName?: string;
  currentShipImo?: string;
  rankDesignation?: string;
  profileImage?: string;
  isActive: boolean;
  emailVerified: boolean;
  accountSetupCompleted: boolean;
  hasSetPassword: boolean;
  ban: boolean;
  banReason?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  permissions?: UpdatePermissionDto | string;
}

export type Gender = "MALE" | "FEMALE" | "OTHER";

export type UserRole = "SEAFARER" | "INSTRUCTOR" | "ADMIN" | "SUPER_ADMIN";

export interface UserFilters {
  search?: string;

  // Status filters
  isActive?: boolean;
  emailVerified?: boolean;
  accountSetupCompleted?: boolean;
  hasSetPassword?: boolean;
  roles?: UserRole[];
  ban?: boolean;

  // Demographic filters
  gender?: Gender;
  ageMin?: number;
  ageMax?: number;
  rankDesignation?: string;
  ranks?: string[];

  // Location/Ship filters
  currentShipName?: string;
  shipNames?: string[];
  currentShipImo?: string;

  // Activity filters
  lastLoginAfter?: Date;
  lastLoginBefore?: Date;
  createdAfter?: Date;
  createdBefore?: Date;
  mobileNumber?: number;
  // Learning progress filters
  hasCompletedCourses?: boolean;
  completedCoursesMin?: number;

  // Pagination and sorting
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface CreateUserDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  indianCdc?: string;
  permissions?: UpdatePermissionDto;
}

export interface UpdateUserDto {
  name?: string;
  age?: number;
  gender?: Gender;
  aadhaarNumber?: string;
  seafarerSid?: string;
  currentShipName?: string;
  currentShipImo?: string;
  rankDesignation?: string;
  profileImageUrl?: string;
}

export interface UpdatePermissionDto {
  [key: string]: any; // Based on your permissions interface
}

export interface BanUserDto {
  ban: boolean;
  reason: string;
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

// ============================================================================
// AXIOS CONFIGURATION
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

const userApiClient = axios.create({
  baseURL: `${API_BASE_URL}/api/users`,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Response interceptor for error handling
// Registered first so it sees the raw AxiosError (with response.status
// and Retry-After) before the mapper below reshapes it.
attachRateLimitRetry(userApiClient);

userApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const status = error.response?.status || 0;

    if (status === 401) {
      localStorage.clear();
      sessionStorage.clear();
      // Avoid hard reload if already on the public login route
      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        window.location.href = "/";
      }
      return;
    }

    const errorData = error.response?.data as
      | { message?: string; error?: string }
      | undefined;

    const apiError: ApiError = {
      message: errorData?.message || error.message || "An error occurred",
      statusCode: status,
      error: errorData?.error,
    };

    return Promise.reject(apiError);
  }
);

// ============================================================================
// USER API FUNCTIONS
// ============================================================================

/**
 * Get the current authenticated user's profile
 * @returns Promise<ApiResponse<User>> - Current user data
 */
export async function getCurrentUser(): Promise<ApiResponse<User>> {
  const response = await userApiClient.get<ApiResponse<User>>("/me");
  if (response.status === 401) {
    localStorage.clear();
    sessionStorage.clear();
  }
  return response.data;
}

export async function updateUserPermissions(
  userId: string,
  permissions: UpdatePermissionDto
): Promise<ApiResponse<User>> {
  const response = await userApiClient.patch<ApiResponse<User>>(
    `/permissions/${encodeURIComponent(userId)}`,
    permissions
  );
  return response.data;
}

/**
 * Update the current authenticated user's profile
 * @param updateData - User profile data to update
 * @returns Promise<ApiResponse<User>> - Updated user data
 */
export async function updateCurrentUser(
  updateData: UpdateUserDto
): Promise<ApiResponse<User>> {
  const response = await userApiClient.patch<ApiResponse<User>>(
    "/me",
    updateData
  );
  return response.data;
}

/**
 * Get a specific user by ID (requires Level 2+ permissions)
 * @param userId - The ID of the user to retrieve
 * @returns Promise<ApiResponse<User>> - User data
 */
export async function getUser(userId: string): Promise<ApiResponse<User>> {
  try {
    const response = await userApiClient.get<User | ApiResponse<User>>(
      `/${encodeURIComponent(userId)}`
    );

    // Handle both response formats:
    // 1. Direct user object response
    // 2. Wrapped in { success, data, message } format
    let userData: User | undefined;
    let success = true;
    let message = "User fetched successfully";

    if (response.data && typeof response.data === "object") {
      if ("id" in response.data) {
        // Direct user object response
        userData = response.data as User;
      } else if ("data" in response.data) {
        // Wrapped response format
        const apiResponse = response.data as ApiResponse<User>;
        userData = apiResponse.data;
        success = apiResponse.success !== false;
        message = apiResponse.message || message;
      }
    }

    if (!userData) {
      throw new Error("Invalid user data format received from server");
    }

    return {
      success,
      message,
      data: userData,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch user",
        data: undefined,
      };
    }
    throw error;
  }
}

/**
 * Get all users with optional filtering and pagination (requires Level 2+ permissions)
 * @param filters - Optional filters to apply to the user search
 * @returns Promise<ApiResponse<User[]>> - Paginated list of users
 */
export async function getUsers(
  filters: UserFilters = {}
): Promise<ApiResponse<User[]>> {
  // Convert filter object to query parameters
  const params = new URLSearchParams();

  // Basic filters
  if (filters.search) params.append("search", filters.search);
  if (filters.isActive !== undefined)
    params.append("isActive", filters.isActive.toString());
  if (filters.emailVerified !== undefined)
    params.append("emailVerified", filters.emailVerified.toString());
  if (filters.accountSetupCompleted !== undefined)
    params.append(
      "accountSetupCompleted",
      filters.accountSetupCompleted.toString()
    );
  if (filters.hasSetPassword !== undefined)
    params.append("hasSetPassword", filters.hasSetPassword.toString());
  if (filters.ban !== undefined) params.append("ban", filters.ban.toString());

  // Array filters (comma-separated)
  if (filters.roles?.length) params.append("roles", filters.roles.join(","));
  if (filters.ranks?.length) params.append("ranks", filters.ranks.join(","));
  if (filters.shipNames?.length)
    params.append("shipNames", filters.shipNames.join(","));

  // Demographic filters
  if (filters.gender) params.append("gender", filters.gender);
  if (filters.ageMin !== undefined)
    params.append("ageMin", filters.ageMin.toString());
  if (filters.ageMax !== undefined)
    params.append("ageMax", filters.ageMax.toString());
  if (filters.rankDesignation)
    params.append("rankDesignation", filters.rankDesignation);
  if (filters.mobileNumber)
    params.append("mobileNumber", filters.mobileNumber.toString());

  // Location/Ship filters
  if (filters.currentShipName)
    params.append("currentShipName", filters.currentShipName);
  if (filters.currentShipImo)
    params.append("currentShipImo", filters.currentShipImo);

  // Date filters
  if (filters.lastLoginAfter)
    params.append("lastLoginAfter", filters.lastLoginAfter.toISOString());
  if (filters.lastLoginBefore)
    params.append("lastLoginBefore", filters.lastLoginBefore.toISOString());
  if (filters.createdAfter)
    params.append("createdAfter", filters.createdAfter.toISOString());
  if (filters.createdBefore)
    params.append("createdBefore", filters.createdBefore.toISOString());

  // Pagination and sorting
  if (filters.page !== undefined)
    params.append("page", filters.page.toString());
  if (filters.limit !== undefined)
    params.append("limit", filters.limit.toString());
  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

  const response = await userApiClient.get<ApiResponse<User[]>>(
    `?${params.toString()}`
  );
  return response.data;
}

/**
 * Create a new user (Super Admin only - requires Level 3 permissions)
 * @param createData - User creation data
 * @returns Promise<ApiResponse<User>> - Created user data
 */
export async function createUser(
  createData: CreateUserDto
): Promise<ApiResponse<User>> {
  const response = await userApiClient.post<ApiResponse<User>>("", createData);
  return response.data;
}

/**
 * Ban or unban a user (Super Admin only - requires Level 3 permissions)
 * @param userId - The ID of the user to ban/unban
 * @param banData - Ban status and reason
 * @returns Promise<ApiResponse<User>> - Updated user data
 */
export async function updateUserBanStatus(
  userId: string,
  banData: BanUserDto
): Promise<ApiResponse<User>> {
  const response = await userApiClient.patch<ApiResponse<User>>(
    `/${encodeURIComponent(userId)}/ban`,
    banData
  );
  return response.data;
}

/**
 * Fields an admin may change on another user. Wider than UpdateUserDto — the
 * self-service shape — because it carries the identity fields a user must not
 * be able to rewrite for themselves.
 */
export interface AdminUpdateUserDto {
  name?: string;
  email?: string;
  indianCdc?: string;
  age?: number;
  gender?: string;
  mobileNumber?: string;
  aadhaarNumber?: string;
  currentShipName?: string;
  currentShipImo?: string;
  rankDesignation?: string;
  role?: string;
}

/**
 * Update another user's information (Admin only - requires Level 2+).
 *
 * Previously this PATCHed `/me` and ignored userId entirely, so an admin edit
 * silently rewrote the admin's own row instead of the target's.
 *
 * @param userId - The ID of the user to update
 * @param updateData - The fields to change
 */
export async function updateUser(
  userId: string,
  updateData: AdminUpdateUserDto
): Promise<ApiResponse<User>> {
  if (!userId?.trim()) {
    throw new Error("User ID is required");
  }

  try {
    const response = await userApiClient.patch<ApiResponse<User>>(
      `/${encodeURIComponent(userId)}`,
      updateData
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || "Failed to update user");
    }
    throw error;
  }
}

/**
 * Set another user's password (Admin only - requires Level 2+).
 * Takes effect immediately and clears any login lockout.
 */
export async function setUserPassword(
  userId: string,
  newPassword: string
): Promise<ApiResponse<{ message: string }>> {
  if (!userId?.trim()) {
    throw new Error("User ID is required");
  }

  try {
    const response = await userApiClient.patch<ApiResponse<{ message: string }>>(
      `/${encodeURIComponent(userId)}/password`,
      { newPassword }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message || "Failed to update password"
      );
    }
    throw error;
  }
}

/**
 * Delete a user permanently (Super Admin only - requires Level 3 permissions)
 * @param userId - The ID of the user to delete
 * @returns Promise<AxiosResponse> - Axios response object
 */
export async function deleteUser(userId: string): Promise<AxiosResponse> {
  try {
    const response = await userApiClient.delete<
      ApiResponse<{ success: boolean; message: string }>
    >(`/${userId}/delete`);
    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || "Failed to delete user");
    }
    throw error;
  }
}

/**
 * Get user stats (Super Admin only - requires Level 2 permissions)
 * @returns Promise<any> - User stats
 */
interface UserStats {
  /**
   * True until the server's snapshot cron has computed the figures once
   * (right after a deploy); every KPI below is then null.
   */
  pending: boolean;
  /** When the snapshot was computed (refreshed every 15 minutes). */
  computedAt: string | null;
  totalUsers: number | null;
  rolesSummary: {
    role: string;
    count: number;
    percentage: string;
  }[];
  totalActiveCourses: number | null;
  /** Users with emailVerified = true (Manage Users "Verified"). */
  verifiedUsers: number | null;
  /** Users certified on every active course that has an assessment. */
  usersCompletedAllCourses: number | null;
  /** Everyone else, so this plus usersCompletedAllCourses equals totalUsers. */
  usersNotCompleted: number | null;
  /** Active vs draft course counts, for the dashboard pie chart. */
  courseStatus: {
    active: number;
    draft: number;
  } | null;
  /** Signups per month for the last 6 months, oldest first, as `YYYY-MM`. */
  userGrowth: {
    month: string;
    count: number;
  }[];
  message: string;
  success: boolean;
}

export async function getUserStats(): Promise<UserStats> {
  const response = await userApiClient.get("/stats");
  return response.data;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Search users by name, email, or other searchable fields
 * @param searchTerm - The term to search for
 * @param additionalFilters - Optional additional filters to apply
 * @returns Promise<User[]> - Array of matching users (without pagination wrapper)
 */
export async function searchUsers(
  searchTerm: string,
  additionalFilters: Omit<UserFilters, "search"> = {}
): Promise<User[]> {
  const response = await getUsers({
    search: searchTerm,
    ...additionalFilters,
  });
  return response.data || [];
}
